import { useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useSites } from '../hooks/useSites'
import { useCameras } from '../hooks/useCameras'
import { GridViewer } from '../components/viewer/GridViewer'
import { PtzPanel } from '../components/viewer/PtzPanel'
import { RecordingPanel } from '../components/viewer/RecordingPanel'
import { EditCameraDialog } from '../components/camera/EditCameraDialog'
import { CameraSettingsPanel } from '../components/viewer/CameraSettingsPanel'
import { Skeleton } from '../components/ui/Skeleton'
import { useToast } from '../components/ui/Toast'
import type { Camera } from '../types'

type RightPanel = { type: 'ptz'; camera: Camera } | { type: 'recording'; camera: Camera } | null

export function ViewerPage() {
  const { id } = useParams<{ id: string }>()
  const { sites } = useSites()
  const { cameras, loading, updateCamera } = useCameras(id)
  const { toast } = useToast()
  const [panel, setPanel] = useState<RightPanel>(null)
  const [editCamera, setEditCamera] = useState<Camera | null>(null)
  const [settingsCamera, setSettingsCamera] = useState<Camera | null>(null)

  const handleSaveCamera = useCallback(async (
    cameraId: string,
    updates: Partial<Pick<Camera, 'name' | 'cam_ip' | 'port' | 'external_ip' | 'rtsp_path' | 'username' | 'password'>>,
  ) => {
    await updateCamera(cameraId, updates)
    toast('Camera updated', 'success')
  }, [updateCamera, toast])

  const site = sites.find((s) => s.id === id)

  if (!site) {
    return (
      <div className="p-6 text-gray-500">
        <p>Site not found.</p>
        <Link to="/" className="text-brand hover:underline text-sm">Back to Dashboard</Link>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-content flex items-center justify-center">
        <div className="space-y-3 w-64">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 flex bg-content">
      {/* Viewer takes full width minus panel */}
      <div className={`flex-1 flex flex-col min-w-0 ${panel ? 'mr-72' : ''}`}>
        {/* Header bar */}
        <div className="flex items-center gap-3 px-4 py-2 bg-sidebar/50 border-b border-card-border">
          <Link to={`/sites/${site.id}`} className="text-gray-500 hover:text-white text-sm">
            &larr; {site.name}
          </Link>
        </div>

        {/* Grid viewer */}
        <div className="flex-1 min-h-0">
          <GridViewer
            site={site}
            cameras={cameras}
            onPtzOpen={(cam) => setPanel({ type: 'ptz', camera: cam })}
            onRecordingOpen={(cam) => setPanel({ type: 'recording', camera: cam })}
            onEditCamera={(cam) => setEditCamera(cam)}
            onSettingsOpen={(cam) => setSettingsCamera(cam)}
          />
        </div>
      </div>

      {/* Right panel — PTZ or Recording */}
      {panel?.type === 'ptz' && (
        <PtzPanel
          camera={panel.camera}
          onClose={() => setPanel(null)}
          onOpenSettings={() => { setSettingsCamera(panel.camera); setPanel(null) }}
        />
      )}
      {panel?.type === 'recording' && (
        <RecordingPanel camera={panel.camera} onClose={() => setPanel(null)} />
      )}
      {/* Edit camera dialog */}
      <EditCameraDialog
        open={!!editCamera}
        camera={editCamera}
        onClose={() => setEditCamera(null)}
        onSave={handleSaveCamera}
      />
      {/* Camera settings (detection zones etc.) */}
      {settingsCamera && (
        <CameraSettingsPanel
          camera={settingsCamera}
          onClose={() => setSettingsCamera(null)}
        />
      )}
    </div>
  )
}
